import React, { useCallback, useEffect, useState } from 'react'
import Input from 'components/common/input/Input'
import CustomModal from 'components/common/modal/CustomModal'
import CustomToast from 'components/common/custom-toast/CustomToast'
import { minioSingleFileUpload } from 'containers/events/Api'
import { alertTypes } from 'constants/Common'
import PropTypes from 'prop-types'
import { CloudArrowUp } from 'phosphor-react'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'

const DEFAULT_PRIMARY = '#006c35'
const DEFAULT_ACCENT = '#c8a84e'

const normalizeColorValue = (value, fallback) => {
  const trimmed = String(value || '').trim()
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return trimmed
  }
  return fallback
}

const getOrganizationId = (organization) =>
  organization?.organizationId || organization?.id || ''

const getOrganizationName = (organization) =>
  organization?.org_name ||
  organization?.name ||
  organization?.organizationName ||
  ''

function AddOrganizationModal({
  show,
  onHide,
  onSubmit,
  saving,
  organization = null,
}) {
  const isEditMode = Boolean(organization)
  const [name, setName] = useState('')
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY)
  const [gradientFirstColor, setGradientFirstColor] = useState(DEFAULT_PRIMARY)
  const [eventCategoryAccent, setEventCategoryAccent] = useState(DEFAULT_ACCENT)
  const [logo, setLogo] = useState('')
  const [uploadingLogo, setUploadingLogo] = useState(false)

  useEffect(() => {
    if (!show) return

    if (isEditMode) {
      setName(getOrganizationName(organization))
      setPrimary(normalizeColorValue(organization.primary, DEFAULT_PRIMARY))
      setGradientFirstColor(
        normalizeColorValue(
          organization.gradientFirstColor || organization.primary,
          DEFAULT_PRIMARY
        )
      )
      setEventCategoryAccent(
        normalizeColorValue(organization.eventCategoryAccent, DEFAULT_ACCENT)
      )
      setLogo(organization.logo || '')
      return
    }

    setName('')
    setPrimary(DEFAULT_PRIMARY)
    setGradientFirstColor(DEFAULT_PRIMARY)
    setEventCategoryAccent(DEFAULT_ACCENT)
    setLogo('')
  }, [isEditMode, organization, show])

  const resetForm = () => {
    setName('')
    setPrimary(DEFAULT_PRIMARY)
    setGradientFirstColor(DEFAULT_PRIMARY)
    setEventCategoryAccent(DEFAULT_ACCENT)
    setLogo('')
  }

  const handleClose = () => {
    resetForm()
    onHide()
  }

  const handleLogoUpload = useCallback(async (file) => {
    const formData = new FormData()
    formData.append('file', file, 'file')
    formData.append('project', 'xler')
    formData.append('fileType', file.type)

    setUploadingLogo(true)
    try {
      const result = await minioSingleFileUpload(formData, () => {})
      if (result?.message && result?.paths) {
        setLogo(result.paths)
        toast(
          <CustomToast
            variant={alertTypes.SUCCESS}
            message={result?.message || 'Logo uploaded successfully'}
          />
        )
        return
      }

      toast(
        <CustomToast
          variant={alertTypes.DANGER}
          message={result?.response?.data?.error || 'Logo upload failed'}
        />
      )
    } finally {
      setUploadingLogo(false)
    }
  }, [])

  const onDrop = useCallback(
    (files) => {
      const [first] = files || []
      if (first) handleLogoUpload(first)
    },
    [handleLogoUpload]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: uploadingLogo || saving,
    accept: {
      'image/png': [],
      'image/jpeg': [],
      'image/jpg': [],
      'image/webp': [],
      'image/svg+xml': [],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
  })

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!isEditMode && !name.trim()) return

    const payload = {
      primary,
      gradientFirstColor,
      eventCategoryAccent,
      logo,
    }

    if (!isEditMode) {
      payload.name = name.trim()
    } else {
      payload.organizationId = getOrganizationId(organization)
    }

    const didSubmit = await onSubmit(payload, isEditMode)

    if (didSubmit) {
      resetForm()
    }
  }

  const canSubmit =
    (isEditMode || name.trim().length > 0) &&
    primary.trim().length > 0 &&
    gradientFirstColor.trim().length > 0 &&
    eventCategoryAccent.trim().length > 0 &&
    !uploadingLogo &&
    !saving

  return (
    <CustomModal
      size='md'
      show={show}
      onHide={handleClose}
      heading={isEditMode ? 'Edit Organization' : 'Add Organization'}
    >
      <form className='form-main organization-create-form' onSubmit={handleSubmit}>
        <div className='row'>
          {isEditMode ? (
            <div className='col-md-12'>
              <div className='field-wrapper'>
                <span className='input-label'>Organization</span>
                <div className='organization-edit-name'>{name}</div>
              </div>
            </div>
          ) : (
            <div className='col-md-12'>
              <div className='field-wrapper'>
                <Input
                  name='name'
                  handleChange={(event) => setName(event.target.value)}
                  placeholder='Organization name'
                  label='Organization name *'
                  value={name}
                />
              </div>
            </div>
          )}

          <div className='col-md-6'>
            <div className='field-wrapper organization-color-field'>
              <label htmlFor='organization-primary-color' className='input-label'>
                Primary color *
              </label>
              <div className='organization-color-field__control'>
                <input
                  id='organization-primary-color'
                  type='color'
                  value={primary}
                  onChange={(event) => setPrimary(event.target.value)}
                  aria-label='Primary color picker'
                />
                <input
                  type='text'
                  className='theme-input input w-100'
                  value={primary}
                  onChange={(event) => setPrimary(event.target.value)}
                  placeholder='#006c35'
                />
              </div>
            </div>
          </div>

          <div className='col-md-6'>
            <div className='field-wrapper organization-color-field'>
              <label
                htmlFor='organization-gradient-first-color'
                className='input-label'
              >
                Gradient First color *
              </label>
              <div className='organization-color-field__control'>
                <input
                  id='organization-gradient-first-color'
                  type='color'
                  value={gradientFirstColor}
                  onChange={(event) =>
                    setGradientFirstColor(event.target.value)
                  }
                  aria-label='Gradient First color picker'
                />
                <input
                  type='text'
                  className='theme-input input w-100'
                  value={gradientFirstColor}
                  onChange={(event) =>
                    setGradientFirstColor(event.target.value)
                  }
                  placeholder='#006c35'
                />
              </div>
            </div>
          </div>

          <div className='col-md-6'>
            <div className='field-wrapper organization-color-field'>
              <label
                htmlFor='organization-accent-color'
                className='input-label'
              >
                Gradient second color *
              </label>
              <div className='organization-color-field__control'>
                <input
                  id='organization-accent-color'
                  type='color'
                  value={eventCategoryAccent}
                  onChange={(event) =>
                    setEventCategoryAccent(event.target.value)
                  }
                  aria-label='Gradient second color picker'
                />
                <input
                  type='text'
                  className='theme-input input w-100'
                  value={eventCategoryAccent}
                  onChange={(event) =>
                    setEventCategoryAccent(event.target.value)
                  }
                  placeholder='#c8a84e'
                />
              </div>
            </div>
          </div>

          <div className='col-md-12'>
            <div className='field-wrapper organization-logo-field'>
              <span className='input-label'>App logo</span>

              {logo ? (
                <div className='organization-logo-preview-card'>
                  <div >
                    <img src={logo} alt='Organization logo preview' width={100} height={100} />
                  </div>
                  <div
                    {...getRootProps()}
                    className={`organization-logo-replace${
                      isDragActive ? ' organization-logo-replace--active' : ''
                    }`}
                  >
                    <input {...getInputProps()} />
                    <CloudArrowUp size={22} weight='duotone' />
                    <div className='organization-logo-replace__text'>
                      <span className='organization-logo-replace__title'>
                        {uploadingLogo ? 'Uploading...' : 'Replace logo'}
                      </span>
                      <span className='organization-logo-replace__hint'>
                        PNG, JPG, WEBP or SVG · max 10MB
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`organization-logo-upload${
                    isDragActive ? ' organization-logo-upload--active' : ''
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className='organization-logo-upload__icon'>
                    <CloudArrowUp size={32} weight='duotone' />
                  </div>
                  <div className='organization-logo-upload__text'>
                    <span className='organization-logo-upload__title'>
                      {uploadingLogo
                        ? 'Uploading...'
                        : 'Drag & drop your logo here'}
                    </span>
                    <span className='organization-logo-upload__hint'>
                      or click to browse · PNG, JPG, WEBP or SVG · max 10MB
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className='col-md-12 d-flex justify-content-end gap-4 mt-2'>
            <button
              type='button'
              className='secondary-btn record-btn'
              onClick={handleClose}
              disabled={saving || uploadingLogo}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='primary-btn record-btn'
              disabled={!canSubmit}
            >
              {saving
                ? isEditMode
                  ? 'Saving...'
                  : 'Creating...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Create Organization'}
            </button>
          </div>
        </div>
      </form>
    </CustomModal>
  )
}

AddOrganizationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  organization: PropTypes.shape({
    id: PropTypes.string,
    organizationId: PropTypes.string,
    name: PropTypes.string,
    org_name: PropTypes.string,
    organizationName: PropTypes.string,
    primary: PropTypes.string,
    gradientFirstColor: PropTypes.string,
    eventCategoryAccent: PropTypes.string,
    logo: PropTypes.string,
  }),
}

AddOrganizationModal.defaultProps = {
  organization: null,
}

export default AddOrganizationModal
